import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Shared TanStack Query client. Conventions (see apps/web/README.md):
 * - query keys start with `['w', wsId, '<module>', ...]` for workspace data, `['me']` for the session.
 * - 4xx errors are not retried (they won't fix themselves); network/5xx retry twice.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/** Query-key helpers — use these so invalidation stays consistent across modules. */
export const qk = {
  me: ['me'] as const,
  authConfig: ['auth', 'config'] as const,
  /** Workspace detail. Invalidate ['w', wsId] to refresh everything in a workspace. */
  workspace: (wsId: string) => ['w', wsId, 'detail'] as const,
  /** `qk.ws(wsId, 'gifts', 'list', { taxYear })` → ['w', wsId, 'gifts', 'list', { taxYear }] */
  ws: (wsId: string, module: string, ...rest: unknown[]) => ['w', wsId, module, ...rest] as const,
};
